import { useApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  setVendorByProductId,
  selectVendors,
  VendorsByRequestId,
  updateVendorByProductId,
} from 'app/store/checkoutRequests';
import {
  getMarketplaceProductVendorInputById,
  REQUEST_MARKETPLACE_PRODUCT_VENDOR_BY_ID,
} from 'app/graphql/cart-api';
import { Marketplace } from 'app/graphql/generated/cart/graphql';

type LineItemWithProductId = { productId?: string | null };

export interface OrderItem {
  id: string;
  lineItems: LineItemWithProductId[];
  marketplace: Marketplace;
}

const getProductId = (lineItems: LineItemWithProductId[]): string | undefined => {
  const firstItem = lineItems[0];
  if (!firstItem) {
    return undefined;
  }
  if ('productId' in firstItem && firstItem.productId) {
    return firstItem.productId;
  }
  return undefined;
};

export const useOrderVendors = (items: OrderItem[] = []): VendorsByRequestId => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  const vendors = useAppSelector(selectVendors);

  const runQuery = async (productId: string, marketplace: Marketplace) => {
    return await client.query({
      query: REQUEST_MARKETPLACE_PRODUCT_VENDOR_BY_ID,
      variables: getMarketplaceProductVendorInputById(productId, marketplace),
    });
  };

  useEffect(() => {
    if (items.length) {
      items.forEach(async (item) => {
        const productId = getProductId(item.lineItems);
        const orderId = item.id;
        const vendor = vendors[orderId];

        dispatch(setVendorByProductId(orderId));
        if (productId && !vendor) {
          try {
            const result = await runQuery(productId, item.marketplace);
            const title = result?.data?.productByID?.vendor;
            dispatch(updateVendorByProductId(orderId, title));
          } catch (error) {
            Sentry.captureException(error);
            dispatch(updateVendorByProductId(orderId, null));
          }
        }
      });
    }
  }, [items]);

  return vendors;
};
